import { useState, useEffect } from "react";
import { updateUserAttributes, fetchUserAttributes } from "aws-amplify/auth";
import { trackCookieConsentUpdate } from "../utils/analyticsService";
import "../pages/settings/Subscription.css";

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    checkConsentStatus();

    const handleShowPreferences = async () => {
      // Load current preferences from user attributes if authenticated
      try {
        const attributes = await fetchUserAttributes();
        const cookieConsent = attributes["custom:cookie_consent"]
          ? JSON.parse(attributes["custom:cookie_consent"])
          : { analytics: false, marketing: false };

        setPreferences({
          necessary: true,
          analytics: cookieConsent.analytics || false,
          marketing: cookieConsent.marketing || false,
        });
      } catch (_error) {
        // User not authenticated, load from localStorage
        const consent = localStorage.getItem("cookieConsent");
        if (consent) {
          const parsed = JSON.parse(consent);
          setPreferences({
            necessary: true,
            analytics: parsed.analytics || false,
            marketing: parsed.marketing || false,
          });
        }
      }

      setShowBanner(true);
      setShowDetails(true);
    };

    window.addEventListener("showCookiePreferences", handleShowPreferences);
    return () =>
      window.removeEventListener(
        "showCookiePreferences",
        handleShowPreferences,
      );
  }, []);

  const checkConsentStatus = async () => {
    try {
      const consent = localStorage.getItem("cookieConsent");
      if (!consent) {
        setShowBanner(true);
      }
    } catch (_error) {
      console.error("Error checking consent:", _error);
    }
  };

  const saveConsent = async (consentData: ConsentPreferences) => {
    try {
      // Save to localStorage
      localStorage.setItem(
        "cookieConsent",
        JSON.stringify({
          ...consentData,
          timestamp: new Date().toISOString(),
        }),
      );

      try {
        // Update Cognito user attributes (only if authenticated)
        await updateUserAttributes({
          userAttributes: {
            "custom:cookie_consent": JSON.stringify(consentData),
            "custom:consent_date": new Date().toISOString(),
          },
        });
      } catch (_error) {
        // User might not be authenticated - this is optional
        console.debug("Could not update user attributes:", _error);
      }

      try {
        // Track cookie consent with Google Analytics 4
        trackCookieConsentUpdate({
          necessary: consentData.necessary,
          analytics: consentData.analytics,
          marketing: consentData.marketing,
          source: "banner",
        });
      } catch (_error) {
        // Analytics tracking is optional - silently fail
        console.debug("Analytics tracking failed:", _error);
      }

      setShowBanner(false);
      setShowDetails(false);
    } catch (_error) {
      console.error("Error saving consent:", _error);
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: "rgba(0,0,0,0.8)",
        padding: "1rem",
      }}
    >
      <div
        className="current-plan-card"
        style={{
          maxWidth: "700px",
          margin: "0 auto",
          padding: "1.5rem",
        }}
      >
        {!showDetails ? (
          <>
            <p
              style={{
                fontSize: "0.9375rem",
                color: "#495057",
                marginBottom: "1.5rem",
                lineHeight: 1.6,
              }}
            >
              We use cookies to improve your experience and provide personalized
              content. You can manage your preferences or accept all cookies.
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                className="primary-action-button"
                style={{ width: "auto" }}
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
              <button
                className="secondary-action-button"
                style={{ width: "auto" }}
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button
                className="secondary-action-button"
                style={{ width: "auto" }}
                onClick={() => setShowDetails(true)}
              >
                Manage Preferences
              </button>
            </div>
          </>
        ) : (
          <>
            <h4
              style={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "#212529",
                margin: "0 0 1.5rem 0",
                textAlign: "center",
              }}
            >
              Cookie Preferences
            </h4>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div className="notification-setting">
                <div className="notification-setting-info">
                  <div className="notification-setting-title">
                    Necessary Cookies
                  </div>
                  <div className="notification-setting-description">
                    Required for basic site functionality and security.
                  </div>
                </div>
                <span
                  className="plan-badge success"
                  style={{ alignSelf: "center" }}
                >
                  Always Active
                </span>
              </div>

              <div className="current-plan-divider"></div>

              <div className="notification-setting">
                <div className="notification-setting-info">
                  <div className="notification-setting-title">
                    Analytics Cookies
                  </div>
                  <div className="notification-setting-description">
                    Help us understand how you use our site to improve
                    performance.
                  </div>
                </div>
                <label className="notification-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) =>
                      setPreferences((prev) => ({
                        ...prev,
                        analytics: e.target.checked,
                      }))
                    }
                  />
                  <span className="notification-toggle-slider"></span>
                </label>
              </div>

              <div className="current-plan-divider"></div>

              <div className="notification-setting">
                <div className="notification-setting-info">
                  <div className="notification-setting-title">
                    Marketing Cookies
                  </div>
                  <div className="notification-setting-description">
                    Used to deliver relevant ads and marketing communications.
                  </div>
                </div>
                <label className="notification-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) =>
                      setPreferences((prev) => ({
                        ...prev,
                        marketing: e.target.checked,
                      }))
                    }
                  />
                  <span className="notification-toggle-slider"></span>
                </label>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "center",
              }}
            >
              <button
                className="primary-action-button"
                style={{ width: "auto" }}
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
              <button
                className="secondary-action-button"
                style={{ width: "auto" }}
                onClick={() => setShowDetails(false)}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CookieConsent;
